import React, { useEffect, useState } from "react";
import { ModalWrapper } from "./ModalWrapper";
import { POPUP_STYLES } from "../../constants/popupStyles";
import apiService from "../../services/api";
import { useCall } from "../../contexts/CallContext";
import { WhatsAppCallPermissionHint } from "../WhatsAppCallPermissionHint";
import { CallMethodSelector, CallMethod } from "../CallMethodSelector";
import { useWhatsAppCallPermission } from "../../hooks/useWhatsAppCallPermission";

interface PhoneOption {
  label: string;
  value: string;
}

interface MeetingRef {
  id: number;
  patient_id: number;
  patient_name?: string;
  patient_phone?: string;
}

interface Props {
  meeting: MeetingRef;
  token: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

export const CallPatientModal: React.FC<Props> = ({ meeting, token, onClose, onSuccess, onError }) => {
  const [phoneOptions, setPhoneOptions] = useState<PhoneOption[]>([]);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [loadingPhones, setLoadingPhones] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [callMethod, setCallMethod] = useState<CallMethod>("phone");
  const [methodTouched, setMethodTouched] = useState(false);
  const { startWhatsAppCall } = useCall();
  const { permission, setPermission, approved } = useWhatsAppCallPermission({
    token,
    patientId: meeting.patient_id,
    phone: selectedPhone,
  });

  // Until the therapist picks a method themselves, follow the patient's stored
  // consent: an approved patient is best reached on WhatsApp, and defaulting to
  // it saves the therapist noticing the badge and switching every time. It
  // tracks the selected number, so changing to an unapproved one falls back to
  // phone rather than leaving a method that would fail.
  // Guarded on `permission` so a re-fetch (the therapist switching number) does
  // not flip the radio to phone and back while the lookup is in flight.
  useEffect(() => {
    if (!methodTouched && permission) setCallMethod(approved ? "whatsapp" : "phone");
  }, [approved, permission, methodTouched]);

  const handleMethodChange = (method: CallMethod) => {
    setMethodTouched(true);
    setCallMethod(method);
  };

  useEffect(() => {
    const fetchPhones = async () => {
      const options: PhoneOption[] = [];
      try {
        const patient = await apiService.getPatientPage({ token, patient_id: meeting.patient_id });
        const seen = new Set<string>();

        const normalize = (phone: string) => phone.replace(/\D/g, "");

        const add = (label: string, phone: string | undefined | null) => {
          const p = phone?.trim();
          if (!p) return;
          const key = normalize(p);
          if (!seen.has(key)) {
            seen.add(key);
            options.push({ label, value: p });
          }
        };

        add("Patient", patient.phone);
        add("Parent 1", patient.parent_1_phone_number);
        add("Parent 2", patient.parent_2_phone_number);
      } catch {
        // no-op — options stays empty, button will be disabled
      } finally {
        setPhoneOptions(options);
        const defaultPhone = options.find((o) => o.value === meeting.patient_phone)?.value ?? options[0]?.value ?? "";
        setSelectedPhone(defaultPhone);
        setLoadingPhones(false);
      }
    };

    fetchPhones();
  }, [token, meeting.patient_id, meeting.patient_phone]);

  const handleCall = async () => {
    if (callMethod === "whatsapp") {
      void startWhatsAppCall({
        phone: selectedPhone,
        patientId: meeting.patient_id,
        patientName: meeting.patient_name || selectedPhone,
        meetingId: meeting.id,
      });
      // Mirror the phone-call path: run onSuccess (which redirects to the
      // meeting) before closing. The CallWidget is mounted in App.tsx and
      // survives navigation, so the WhatsApp call keeps running after redirect.
      onSuccess();
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await apiService.callPatient({ token, meeting_id: meeting.id, phone: selectedPhone, call_type: "both" });
      onSuccess();
      onClose();
    } catch (e: any) {
      onError(e.response?.data?.message || e.message || "Call failed");
      onClose();
    }
  };

  return (
    <ModalWrapper modalId="CallPatientModal"
      isOpen={true}
      onClose={onClose}
      title="Start Call"
      isLoading={submitting}
      width="max-w-sm"
    >
      <div className="space-y-5">
        {/* Patient name */}
        {meeting.patient_name && (
          <p className="text-sm text-[#366A75] font-medium -mt-2">{meeting.patient_name}</p>
        )}

        <CallMethodSelector value={callMethod} onChange={handleMethodChange} whatsappApproved={approved} />

        {/* Stored call-permission state for the number about to be dialed */}
        {callMethod === "whatsapp" && !loadingPhones && selectedPhone && (
          <WhatsAppCallPermissionHint
            token={token}
            patientId={meeting.patient_id}
            phone={selectedPhone}
            permission={permission}
            onPermissionChange={setPermission}
          />
        )}

        {/* Phone selection */}
        <div>
          <label className="block text-sm font-medium text-[#366A75] mb-2">Phone Number</label>
          {loadingPhones ? (
            <div className="flex items-center gap-2 text-sm text-[#366A75]/70">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#366A75]" />
              Loading phones…
            </div>
          ) : phoneOptions.length === 0 ? (
            <p className="text-sm text-[#366A75]/60">No phone number available</p>
          ) : phoneOptions.length === 1 ? (
            <div className={`${POPUP_STYLES.input} text-sm text-[#366A75]`}>
              <span className="font-medium">{phoneOptions[0].label}:</span> {phoneOptions[0].value}
            </div>
          ) : (
            <div className="space-y-2">
              {phoneOptions.map((opt) => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer px-4 py-2 rounded-lg bg-[#EBF5F5] hover:bg-[#D0E7E5] transition-colors">
                  <input
                    type="radio"
                    name="phone"
                    value={opt.value}
                    checked={selectedPhone === opt.value}
                    onChange={() => setSelectedPhone(opt.value)}
                    className="accent-[#366A75]"
                  />
                  <span className="text-sm text-[#366A75]">
                    <span className="font-medium">{opt.label}</span>
                    <span className="ml-2 opacity-70">{opt.value}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 justify-end pt-1">
          <button onClick={onClose} className={POPUP_STYLES.button} disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleCall}
            disabled={submitting || loadingPhones || !selectedPhone}
            className={POPUP_STYLES.button}
          >
            {submitting ? "Calling…" : callMethod === "whatsapp" ? "WhatsApp Call" : "Call"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
